import {Resource} from 'i18next';

export const resources: Resource = {
  en: {
    translation: {
      common: {
        language: 'Language',
        back: 'Back',
        close: 'Close',
        cancel: 'Cancel',
        updateInProgress: 'Updating...',
        tryAgain: 'Try again',
        loading: 'Loading...',
      },
      languages: {
        en: 'English',
        ar: 'Arabic',
        fr: 'French',
      },
      tabs: {
        Home: 'Home',
        Transactions: 'Transactions',
        Bracelet: 'Bracelet',
        Tips: 'Tips',
        Profile: 'Profile',
      },
      auth: {
        verifyingAccess: 'Verifying your access...',
        welcome: 'Welcome',
        takingToAccount: 'Taking you to your account...',
        alreadyLinked: 'Already linked',
        deviceConflict: 'This QR code is already being used on another device. Please visit reception to get a new QR code.',
        accessExpired: 'Access expired',
        accessExpiredBody: 'Your QR code has expired or been invalidated. Please visit reception for assistance.',
        somethingWentWrong: 'Something went wrong',
        genericError: 'Please try scanning your QR code again or contact reception for assistance.',
      },
      expired: {
        title: 'Your session has ended',
        line1: 'Thank you for staying with us.',
        line2: 'If you are still checked in, please visit reception to scan your QR code again.',
      },
      home: {
        availableBalance: 'Available balance',
        walletSuspended: 'Wallet suspended',
        recentTransactions: 'Recent transactions',
        noTransactionsYet: 'No transactions yet',
        myOfferings: 'My included services',
        offeringUnlimited: 'Unlimited',
        offeringDone: 'Used up',
        offeringRemaining: '{{count}} left',
        checkIn: 'Check-in',
        checkOut: 'Check-out',
        remaining: 'Remaining',
        room: 'Room',
        day_one: '{{count}} day',
        day_other: '{{count}} days',
      },
      transactions: {
        title: 'Transactions',
        filterAll: 'All',
        filterPayments: 'Payments',
        filterTopUps: 'Top-ups',
        filterRefunds: 'Refunds',
        noTransactionsFound: 'No transactions found',
        loadMore: 'Load more',
        detailsTitle: 'Transaction details',
        id: 'ID',
        type: 'Type',
        location: 'Location',
        amount: 'Amount',
        status: 'Status',
        date: 'Date',
        note: 'Note',
        completed: 'Completed',
      },
      bracelet: {
        title: 'My bracelet',
        reportLost: 'Report lost',
        foundBracelet: 'I found my bracelet',
        suspendedByRequest: 'Your account has been suspended by your request. Please visit reception to reactivate.',
        infoNote: 'Your bracelet is your key to cashless payments at the hotel. Keep it safe and report immediately if lost.',
        noBracelet: 'No bracelet found for your account.',
        lostToast: 'Your bracelet has been reported as lost.',
        foundToast: 'Your bracelet is active again.',
        updateFailed: 'Could not update status. Please try again.',
        reportLostTitle: 'Report bracelet as lost?',
        reportLostBody: 'This will immediately suspend your wallet and block all transactions until your bracelet is found.',
        confirmLost: 'Confirm lost',
        foundTitle: 'Bracelet found?',
        foundBody: 'This will reactivate your bracelet and unfreeze your wallet.',
        yesFound: 'Yes, found it',
        assigned: 'Assigned {{date}}',
      },
      profile: {
        title: 'My profile',
        accountDetails: 'Account details',
        accountSection: 'Account',
        accountSuspended: 'Your account is suspended. Visit reception to reactivate.',
        suspendMyAccount: 'Suspend my account',
        needHelp: 'Need help?',
        helpBody: 'For any issues with your bracelet or account, please visit the reception desk or call extension 0.',
        reception: 'Reception',
        receptionHours: 'Open 24/7 - Extension 0',
        profileUnavailable: 'Profile not available.',
        copyUidToast: 'Bracelet UID copied.',
        deactivatedToast: 'Your account has been suspended.',
        deactivateFailed: 'Could not suspend account. Please try again.',
        suspendTitle: 'Suspend your account?',
        suspendBody: 'This will freeze your wallet and suspend all transactions. You can reactivate by visiting reception.',
        suspending: 'Suspending...',
        suspendAccount: 'Suspend account',
        roomLabel: 'Room {{room}}',
      },
      tips: {
        tab: 'Tips',
        pendingTitle: 'Tip requests',
        requestExpired: 'This tip request has expired.',
        confirmAcceptTitle: 'Accept tip?',
        confirmAcceptMessage: '{{worker}} is offering you {{amount}}  as a tip. Would you like to accept?',
        approvedSuccess: 'Tip accepted — thank you!',
        approvedError: 'Could not accept the tip. Please try again.',
        declined: 'Tip declined.',
        voluntaryTitle: 'Send a tip',
        searchPlaceholder: 'Search by name...',
        workersEmpty: 'No staff found matching your search.',
        confirmButton: 'Confirm',
        confirmVoluntaryTitle: 'Send tip to {{worker}}?',
        voluntarySuccess: 'Tip sent successfully!',
        voluntaryError: 'Could not send the tip. Please try again.',
        historyTitle: 'Tip history',
        historyEmpty: 'No tips yet.',
        methodRequested: 'Requested',
        methodVoluntary: 'Voluntary',
        countdownLabel: 'Expires in',
        errors: {
          lengthTooShort: 'Please enter at least 3 characters.',
          lengthExceeded: 'Input is too long.',
        },
      },
      install: {
        promptMessage: 'Add this app to your home screen for quick access.',
        promptButton: 'Add to home screen',
        promptDismiss: 'Not now',
        iosMessage: 'Tap the share icon then "Add to Home Screen" to install this app.',
      },
      family: {
        sectionTitle: 'Family bracelets',
        roleParent: 'Family organizer',
        roleChild: 'Linked member',
        childCount_one: '{{count}} linked member',
        childCount_other: '{{count}} linked members',
        linkedToParent: 'Linked to organizer',
        sendToChild: 'Send to member',
        sendToParent: 'Send back to organizer',
        sendToChildTitle: 'Send funds to member',
        sendToParentTitle: 'Send back to organizer',
        selectMember: 'Select a member',
        amountLabel: 'Amount (DNT)',
        amountPlaceholder: 'Enter amount...',
        confirmSend: 'Confirm transfer',
        sending: 'Sending...',
        sendSuccess: 'Transfer successful.',
        sendError: 'Transfer failed. Please try again.',
        balance: 'Balance',
        childType: {
          MINOR: 'Minor',
          ADULT_PARTNER: 'Partner',
        },
        noChildren: 'No linked members yet.',
        presetAmounts: 'Quick amounts',
      },
      labels: {
        subscription: {
          RESIDENCE: 'Residence guest',
          PISCINE_ONLY: 'Pool access',
          CAFE_ONLY: 'Cafe access',
        },
        txType: {
          TOPUP: 'Top-up',
          PAYMENT: 'Payment',
          REFUND: 'Refund',
          TIP: 'Tip',
          BRACELET_TRANSFER: 'Family transfer',
        },
        braceletStatus: {
          ACTIVE: 'Active',
          LOST: 'Reported lost',
          FROZEN: 'Suspended',
          INACTIVE: 'Inactive',
          FREE: 'Available',
        },
        location: {
          BAR: 'Bar',
          RESTAURANT: 'Restaurant',
          PISCINE_BAR: 'Pool bar',
          RECEPTION: 'Reception',
          HOTEL: 'Hotel',
          unknown: 'Unknown',
        },
      },
      greeting: {
        morning: 'Good morning',
        afternoon: 'Good afternoon',
        evening: 'Good evening',
        withName: '{{greeting}}, {{name}}',
      },
      time: {
        justNow: 'Just now',
        minuteAgo_one: '{{count}} min ago',
        minuteAgo_other: '{{count}} mins ago',
        hourAgo_one: '{{count}} hour ago',
        hourAgo_other: '{{count}} hours ago',
        yesterday: 'Yesterday',
      },
      errors: {
        profileLoad: 'Unable to load your profile. Please try again.',
        walletLoad: 'Unable to load wallet information.',
        braceletLoad: 'Unable to load bracelet information.',
        transactionsLoad: 'Unable to load transactions.',
      },
    },
  },
  ar: {
    translation: {
      common: {
        language: 'اللغة',
        back: 'رجوع',
        close: 'إغلاق',
        cancel: 'إلغاء',
        updateInProgress: 'جار التحديث...',
        tryAgain: 'حاول مرة أخرى',
        loading: 'جار التحميل...',
      },
      languages: {
        en: 'الإنجليزية',
        ar: 'العربية',
        fr: 'الفرنسية',
      },
      tabs: {
        Home: 'الرئيسية',
        Transactions: 'المعاملات',
        Bracelet: 'السوار',
        Tips: 'البقشيش',
        Profile: 'الملف الشخصي',
      },
      auth: {
        verifyingAccess: 'جار التحقق من صلاحية الوصول...',
        welcome: 'مرحبا',
        takingToAccount: 'جار تحويلك إلى حسابك...',
        alreadyLinked: 'تم الربط مسبقا',
        deviceConflict: 'رمز QR هذا مستخدم بالفعل على جهاز آخر. يرجى زيارة الاستقبال للحصول على رمز جديد.',
        accessExpired: 'انتهت صلاحية الوصول',
        accessExpiredBody: 'انتهت صلاحية رمز QR أو تم إلغاؤه. يرجى زيارة الاستقبال للمساعدة.',
        somethingWentWrong: 'حدث خطأ ما',
        genericError: 'يرجى إعادة مسح رمز QR أو التواصل مع الاستقبال للمساعدة.',
      },
      expired: {
        title: 'انتهت جلستك',
        line1: 'شكرا لإقامتك معنا.',
        line2: 'إذا كنت ما زلت مسجلا في الفندق، يرجى زيارة الاستقبال لمسح رمز QR مرة أخرى.',
      },
      home: {
        availableBalance: 'الرصيد المتاح',
        walletSuspended: 'المحفظة معلقة',
        recentTransactions: 'آخر المعاملات',
        noTransactionsYet: 'لا توجد معاملات بعد',
        myOfferings: 'خدماتي المشمولة',
        offeringUnlimited: 'غير محدود',
        offeringDone: 'استُنفد',
        offeringRemaining: 'متبقي {{count}}',
        checkIn: 'تاريخ الدخول',
        checkOut: 'تاريخ المغادرة',
        remaining: 'المتبقي',
        room: 'الغرفة',
        day_one: '{{count}} يوم',
        day_other: '{{count}} أيام',
      },
      transactions: {
        title: 'المعاملات',
        filterAll: 'الكل',
        filterPayments: 'المدفوعات',
        filterTopUps: 'الشحن',
        filterRefunds: 'الاسترجاع',
        noTransactionsFound: 'لم يتم العثور على معاملات',
        loadMore: 'عرض المزيد',
        detailsTitle: 'تفاصيل المعاملة',
        id: 'المعرف',
        type: 'النوع',
        location: 'الموقع',
        amount: 'المبلغ',
        status: 'الحالة',
        date: 'التاريخ',
        note: 'ملاحظة',
        completed: 'مكتملة',
      },
      bracelet: {
        title: 'سواري',
        reportLost: 'الإبلاغ عن الفقدان',
        foundBracelet: 'وجدت سواري',
        suspendedByRequest: 'تم تعليق حسابك بناء على طلبك. يرجى زيارة الاستقبال لإعادة التفعيل.',
        infoNote: 'سوارك هو مفتاحك للدفع غير النقدي في الفندق. حافظ عليه وأبلغ فورا في حال الفقدان.',
        noBracelet: 'لا يوجد سوار مرتبط بحسابك.',
        lostToast: 'تم الإبلاغ عن فقدان السوار.',
        foundToast: 'تمت إعادة تفعيل السوار.',
        updateFailed: 'تعذر تحديث الحالة. يرجى المحاولة مرة أخرى.',
        reportLostTitle: 'هل تريد الإبلاغ عن السوار كمفقود؟',
        reportLostBody: 'سيؤدي ذلك إلى تعليق المحفظة فورا وحظر جميع المعاملات حتى يتم العثور على السوار.',
        confirmLost: 'تأكيد الفقدان',
        foundTitle: 'هل تم العثور على السوار؟',
        foundBody: 'سيؤدي ذلك إلى إعادة تفعيل السوار وإلغاء تجميد المحفظة.',
        yesFound: 'نعم، تم العثور عليه',
        assigned: 'تم التخصيص {{date}}',
      },
      profile: {
        title: 'ملفي الشخصي',
        accountDetails: 'تفاصيل الحساب',
        accountSection: 'الحساب',
        accountSuspended: 'حسابك معلق. زر الاستقبال لإعادة التفعيل.',
        suspendMyAccount: 'تعليق حسابي',
        needHelp: 'تحتاج مساعدة؟',
        helpBody: 'لأي مشكلة في السوار أو الحساب، يرجى زيارة مكتب الاستقبال أو الاتصال بالتحويلة 0.',
        reception: 'الاستقبال',
        receptionHours: 'مفتوح 24/7 - تحويلة 0',
        profileUnavailable: 'الملف الشخصي غير متاح.',
        copyUidToast: 'تم نسخ معرف السوار.',
        deactivatedToast: 'تم تعليق حسابك.',
        deactivateFailed: 'تعذر تعليق الحساب. يرجى المحاولة مرة أخرى.',
        suspendTitle: 'هل تريد تعليق حسابك؟',
        suspendBody: 'سيؤدي ذلك إلى تجميد المحفظة وتعليق جميع المعاملات. يمكنك إعادة التفعيل بزيارة الاستقبال.',
        suspending: 'جار التعليق...',
        suspendAccount: 'تعليق الحساب',
        roomLabel: 'الغرفة {{room}}',
      },
      tips: {
        tab: 'البقشيش',
        pendingTitle: 'طلبات البقشيش',
        requestExpired: 'انتهت صلاحية طلب البقشيش.',
        confirmAcceptTitle: 'قبول البقشيش؟',
        confirmAcceptMessage: 'يعرض عليك {{worker}} مبلغ {{amount}} دينار كبقشيش. هل تريد قبوله؟',
        approvedSuccess: 'تم قبول البقشيش — شكراً لك!',
        approvedError: 'تعذر قبول البقشيش. يرجى المحاولة مرة أخرى.',
        declined: 'تم رفض البقشيش.',
        voluntaryTitle: 'إرسال بقشيش',
        searchPlaceholder: 'ابحث بالاسم...',
        workersEmpty: 'لم يتم العثور على موظفين.',
        confirmButton: 'تأكيد',
        confirmVoluntaryTitle: 'إرسال بقشيش إلى {{worker}}؟',
        voluntarySuccess: 'تم إرسال البقشيش بنجاح!',
        voluntaryError: 'تعذر إرسال البقشيش. يرجى المحاولة مرة أخرى.',
        historyTitle: 'سجل البقشيش',
        historyEmpty: 'لا يوجد بقشيش بعد.',
        methodRequested: 'مطلوب',
        methodVoluntary: 'طوعي',
        countdownLabel: 'ينتهي خلال',
        errors: {
          lengthTooShort: 'يرجى إدخال 3 أحرف على الأقل.',
          lengthExceeded: 'النص المدخل طويل جداً.',
        },
      },
      install: {
        promptMessage: 'أضف هذا التطبيق إلى شاشتك الرئيسية للوصول السريع.',
        promptButton: 'إضافة إلى الشاشة الرئيسية',
        promptDismiss: 'ليس الآن',
        iosMessage: 'اضغط على أيقونة المشاركة ثم "إضافة إلى الشاشة الرئيسية" لتثبيت التطبيق.',
      },
      family: {
        sectionTitle: 'أساور العائلة',
        roleParent: 'منظم العائلة',
        roleChild: 'عضو مرتبط',
        childCount_one: '{{count}} عضو مرتبط',
        childCount_other: '{{count}} أعضاء مرتبطون',
        linkedToParent: 'مرتبط بالمنظم',
        sendToChild: 'إرسال إلى عضو',
        sendToParent: 'إرسال إلى المنظم',
        sendToChildTitle: 'إرسال أموال إلى عضو',
        sendToParentTitle: 'إرسال إلى المنظم',
        selectMember: 'اختر عضوا',
        amountLabel: 'المبلغ (دينار)',
        amountPlaceholder: 'أدخل المبلغ...',
        confirmSend: 'تأكيد التحويل',
        sending: 'جار الإرسال...',
        sendSuccess: 'تم التحويل بنجاح.',
        sendError: 'فشل التحويل. يرجى المحاولة مرة أخرى.',
        balance: 'الرصيد',
        childType: {
          MINOR: 'قاصر',
          ADULT_PARTNER: 'شريك',
        },
        noChildren: 'لا يوجد أعضاء مرتبطون بعد.',
        presetAmounts: 'مبالغ سريعة',
      },
      labels: {
        subscription: {
          RESIDENCE: 'ضيف إقامة',
          PISCINE_ONLY: 'دخول المسبح',
          CAFE_ONLY: 'دخول المقهى',
        },
        txType: {
          TOPUP: 'شحن',
          PAYMENT: 'دفع',
          REFUND: 'استرجاع',
          TIP: 'بقشيش',
          BRACELET_TRANSFER: 'تحويل عائلي',
        },
        braceletStatus: {
          ACTIVE: 'نشط',
          LOST: 'تم الإبلاغ عن الفقدان',
          FROZEN: 'معلق',
          INACTIVE: 'غير نشط',
          FREE: 'متاح',
        },
        location: {
          BAR: 'البار',
          RESTAURANT: 'المطعم',
          PISCINE_BAR: 'بار المسبح',
          RECEPTION: 'الاستقبال',
          HOTEL: 'الفندق',
          unknown: 'غير معروف',
        },
      },
      greeting: {
        morning: 'صباح الخير',
        afternoon: 'مساء الخير',
        evening: 'مساء الخير',
        withName: '{{greeting}}، {{name}}',
      },
      time: {
        justNow: 'الآن',
        minuteAgo_one: 'منذ دقيقة',
        minuteAgo_other: 'منذ {{count}} دقائق',
        hourAgo_one: 'منذ ساعة',
        hourAgo_other: 'منذ {{count}} ساعات',
        yesterday: 'أمس',
      },
      errors: {
        profileLoad: 'تعذر تحميل ملفك الشخصي. يرجى المحاولة مرة أخرى.',
        walletLoad: 'تعذر تحميل معلومات المحفظة.',
        braceletLoad: 'تعذر تحميل معلومات السوار.',
        transactionsLoad: 'تعذر تحميل المعاملات.',
      },
    },
  },
  fr: {
    translation: {
      common: {
        language: 'Langue',
        back: 'Retour',
        close: 'Fermer',
        cancel: 'Annuler',
        updateInProgress: 'Mise a jour...',
        tryAgain: 'Reessayer',
        loading: 'Chargement...',
      },
      languages: {
        en: 'Anglais',
        ar: 'Arabe',
        fr: 'Francais',
      },
      tabs: {
        Home: 'Accueil',
        Transactions: 'Transactions',
        Bracelet: 'Bracelet',
        Tips: 'Pourboires',
        Profile: 'Profil',
      },
      auth: {
        verifyingAccess: 'Verification de votre acces...',
        welcome: 'Bienvenue',
        takingToAccount: 'Redirection vers votre compte...',
        alreadyLinked: 'Deja associe',
        deviceConflict: 'Ce QR code est deja utilise sur un autre appareil. Veuillez passer a la reception pour obtenir un nouveau QR code.',
        accessExpired: 'Acces expire',
        accessExpiredBody: 'Votre QR code a expire ou a ete invalide. Veuillez contacter la reception.',
        somethingWentWrong: 'Une erreur est survenue',
        genericError: 'Veuillez rescanner votre QR code ou contacter la reception.',
      },
      expired: {
        title: 'Votre session est terminee',
        line1: 'Merci d avoir sejourne chez nous.',
        line2: 'Si vous etes toujours enregistre, veuillez passer a la reception pour rescanner votre QR code.',
      },
      home: {
        availableBalance: 'Solde disponible',
        walletSuspended: 'Portefeuille suspendu',
        recentTransactions: 'Transactions recentes',
        noTransactionsYet: 'Aucune transaction pour le moment',
        myOfferings: 'Mes services inclus',
        offeringUnlimited: 'Illimite',
        offeringDone: 'Epuise',
        offeringRemaining: '{{count}} restant',
        checkIn: 'Arrivee',
        checkOut: 'Depart',
        remaining: 'Restant',
        room: 'Chambre',
        day_one: '{{count}} jour',
        day_other: '{{count}} jours',
      },
      transactions: {
        title: 'Transactions',
        filterAll: 'Toutes',
        filterPayments: 'Paiements',
        filterTopUps: 'Recharges',
        filterRefunds: 'Remboursements',
        noTransactionsFound: 'Aucune transaction trouvee',
        loadMore: 'Charger plus',
        detailsTitle: 'Details de la transaction',
        id: 'ID',
        type: 'Type',
        location: 'Lieu',
        amount: 'Montant',
        status: 'Statut',
        date: 'Date',
        note: 'Note',
        completed: 'Terminee',
      },
      bracelet: {
        title: 'Mon bracelet',
        reportLost: 'Signaler perdu',
        foundBracelet: 'J ai retrouve mon bracelet',
        suspendedByRequest: 'Votre compte a ete suspendu a votre demande. Veuillez passer a la reception pour le reactiver.',
        infoNote: 'Votre bracelet est votre cle pour les paiements sans especes a l hotel. Conservez-le en securite et signalez toute perte immediatement.',
        noBracelet: 'Aucun bracelet associe a votre compte.',
        lostToast: 'Votre bracelet a ete signale perdu.',
        foundToast: 'Votre bracelet est de nouveau actif.',
        updateFailed: 'Impossible de mettre a jour le statut. Veuillez reessayer.',
        reportLostTitle: 'Signaler le bracelet comme perdu ?',
        reportLostBody: 'Cela suspendra immediatement votre portefeuille et bloquera toutes les transactions jusqu a ce que le bracelet soit retrouve.',
        confirmLost: 'Confirmer la perte',
        foundTitle: 'Bracelet retrouve ?',
        foundBody: 'Cela reactivera votre bracelet et debloquera votre portefeuille.',
        yesFound: 'Oui, retrouve',
        assigned: 'Attribue le {{date}}',
      },
      profile: {
        title: 'Mon profil',
        accountDetails: 'Details du compte',
        accountSection: 'Compte',
        accountSuspended: 'Votre compte est suspendu. Passez a la reception pour le reactiver.',
        suspendMyAccount: 'Suspendre mon compte',
        needHelp: 'Besoin d aide ?',
        helpBody: 'Pour tout probleme de bracelet ou de compte, veuillez passer a la reception ou appeler l extension 0.',
        reception: 'Reception',
        receptionHours: 'Ouvert 24/7 - Extension 0',
        profileUnavailable: 'Profil non disponible.',
        copyUidToast: 'UID du bracelet copie.',
        deactivatedToast: 'Votre compte a ete suspendu.',
        deactivateFailed: 'Impossible de suspendre le compte. Veuillez reessayer.',
        suspendTitle: 'Suspendre votre compte ?',
        suspendBody: 'Cela figera votre portefeuille et suspendra toutes les transactions. Vous pouvez reactiver en passant a la reception.',
        suspending: 'Suspension...',
        suspendAccount: 'Suspendre le compte',
        roomLabel: 'Chambre {{room}}',
      },
      tips: {
        tab: 'Pourboires',
        pendingTitle: 'Demandes de pourboire',
        requestExpired: 'Cette demande de pourboire a expire.',
        confirmAcceptTitle: 'Accepter le pourboire ?',
        confirmAcceptMessage: '{{worker}} vous propose {{amount}}  en pourboire. Souhaitez-vous l accepter ?',
        approvedSuccess: 'Pourboire accepte — merci !',
        approvedError: 'Impossible d accepter le pourboire. Veuillez reessayer.',
        declined: 'Pourboire refuse.',
        voluntaryTitle: 'Envoyer un pourboire',
        searchPlaceholder: 'Rechercher par nom...',
        workersEmpty: 'Aucun membre du personnel trouve.',
        confirmButton: 'Confirmer',
        confirmVoluntaryTitle: 'Envoyer un pourboire a {{worker}} ?',
        voluntarySuccess: 'Pourboire envoye avec succes !',
        voluntaryError: 'Impossible d envoyer le pourboire. Veuillez reessayer.',
        historyTitle: 'Historique des pourboires',
        historyEmpty: 'Aucun pourboire pour le moment.',
        methodRequested: 'Demande',
        methodVoluntary: 'Volontaire',
        countdownLabel: 'Expire dans',
        errors: {
          lengthTooShort: 'Veuillez saisir au moins 3 caracteres.',
          lengthExceeded: 'La saisie est trop longue.',
        },
      },
      install: {
        promptMessage: 'Ajoutez cette application a votre ecran d accueil pour un acces rapide.',
        promptButton: 'Ajouter a l ecran d accueil',
        promptDismiss: 'Pas maintenant',
        iosMessage: 'Appuyez sur l icone de partage puis sur "Ajouter a l ecran d accueil" pour installer l application.',
      },
      family: {
        sectionTitle: 'Bracelets famille',
        roleParent: 'Organisateur famille',
        roleChild: 'Membre associe',
        childCount_one: '{{count}} membre associe',
        childCount_other: '{{count}} membres associes',
        linkedToParent: 'Associe a l organisateur',
        sendToChild: 'Envoyer a un membre',
        sendToParent: 'Renvoyer a l organisateur',
        sendToChildTitle: 'Envoyer des fonds a un membre',
        sendToParentTitle: 'Renvoyer a l organisateur',
        selectMember: 'Selectionner un membre',
        amountLabel: 'Montant (DNT)',
        amountPlaceholder: 'Saisir le montant...',
        confirmSend: 'Confirmer le virement',
        sending: 'Envoi...',
        sendSuccess: 'Virement effectue.',
        sendError: 'Virement echoue. Veuillez reessayer.',
        balance: 'Solde',
        childType: {
          MINOR: 'Mineur',
          ADULT_PARTNER: 'Partenaire',
        },
        noChildren: 'Aucun membre associe.',
        presetAmounts: 'Montants rapides',
      },
      labels: {
        subscription: {
          RESIDENCE: 'Client resident',
          PISCINE_ONLY: 'Acces piscine',
          CAFE_ONLY: 'Acces cafe',
        },
        txType: {
          TOPUP: 'Recharge',
          PAYMENT: 'Paiement',
          REFUND: 'Remboursement',
          TIP: 'Pourboire',
          BRACELET_TRANSFER: 'Virement famille',
        },
        braceletStatus: {
          ACTIVE: 'Actif',
          LOST: 'Signale perdu',
          FROZEN: 'Suspendu',
          INACTIVE: 'Inactif',
          FREE: 'Disponible',
        },
        location: {
          BAR: 'Bar',
          RESTAURANT: 'Restaurant',
          PISCINE_BAR: 'Bar piscine',
          RECEPTION: 'Reception',
          HOTEL: 'Hotel',
          unknown: 'Inconnu',
        },
      },
      greeting: {
        morning: 'Bonjour',
        afternoon: 'Bon apres-midi',
        evening: 'Bonsoir',
        withName: '{{greeting}}, {{name}}',
      },
      time: {
        justNow: 'A l instant',
        minuteAgo_one: 'Il y a {{count}} min',
        minuteAgo_other: 'Il y a {{count}} min',
        hourAgo_one: 'Il y a {{count}} heure',
        hourAgo_other: 'Il y a {{count}} heures',
        yesterday: 'Hier',
      },
      errors: {
        profileLoad: 'Impossible de charger votre profil. Veuillez reessayer.',
        walletLoad: 'Impossible de charger les informations du portefeuille.',
        braceletLoad: 'Impossible de charger les informations du bracelet.',
        transactionsLoad: 'Impossible de charger les transactions.',
      },
    },
  },
};

